import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { AcademicEvent, EventCategory } from '../../services/annualPlanningService';
import { calendarSettingsService, AcademicYearTemplate, RamadanDates } from '../../services/calendarSettingsService';
import { PublicHoliday } from '../../services/holidaysService';

interface AcademicYearCalendarViewProps {
  academicYearStart: number;
  events: AcademicEvent[];
  categories: EventCategory[];
  holidays: PublicHoliday[];
  selectedCategoryIds?: string[]; // For filtering - if empty, show all
  onYearChange: (startYear: number) => void;
  onEventClick: (event: AcademicEvent) => void;
  onDateClick?: (date: Date) => void;
  onDateRangeSelect?: (startDate: Date, endDate: Date) => void;
  onHolidayClick?: (holiday: PublicHoliday) => void;
  isEditMode?: boolean;
}

interface DragState {
  isSelecting: boolean;
  startDate: Date | null;
  endDate: Date | null;
  monthIndex: number | null;
  monthYear: number | null;
}

const ALL_MONTHS = [
  { index: 0, name: 'Janvier', abbr: 'Jan' },
  { index: 1, name: 'Février', abbr: 'Fév' },
  { index: 2, name: 'Mars', abbr: 'Mar' },
  { index: 3, name: 'Avril', abbr: 'Avr' },
  { index: 4, name: 'Mai', abbr: 'Mai' },
  { index: 5, name: 'Juin', abbr: 'Juin' },
  { index: 6, name: 'Juillet', abbr: 'Juil' },
  { index: 7, name: 'Août', abbr: 'Août' },
  { index: 8, name: 'Septembre', abbr: 'Sept' },
  { index: 9, name: 'Octobre', abbr: 'Oct' },
  { index: 10, name: 'Novembre', abbr: 'Nov' },
  { index: 11, name: 'Décembre', abbr: 'Déc' },
];

interface MonthInfo {
  index: number;
  name: string;
  abbr: string;
  year: number;
  isBuffer?: boolean;
  position?: 'before' | 'after';
}

interface EventBar {
  event: AcademicEvent | PublicHoliday;
  type: 'event' | 'holiday' | 'marker';
  startDay: number;
  endDay: number;
  color: string;
  title: string;
  lane: number; // Which row/lane this event should be displayed on
  categoryLabel?: string; // For display in tooltip
}
// Note: Ramadan is NOT displayed as a bar - only as background color on cells

export const AcademicYearCalendarView: React.FC<AcademicYearCalendarViewProps> = ({
  academicYearStart,
  events,
  categories,
  holidays,
  selectedCategoryIds: _selectedCategoryIds,
  onYearChange,
  onEventClick,
  onDateClick,
  onDateRangeSelect,
  onHolidayClick,
  // isEditMode is kept for backward compatibility but no longer required for direct click
}) => {
  const [template, setTemplate] = useState<AcademicYearTemplate | null>(null);
  const [ramadanDates, setRamadanDates] = useState<RamadanDates | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number; showAbove: boolean } | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Drag selection state for multi-day events
  const [dragState, setDragState] = useState<DragState>({
    isSelecting: false,
    startDate: null,
    endDate: null,
    monthIndex: null,
    monthYear: null,
  });

  // Check if user can add events (has onDateClick or onDateRangeSelect)
  const canAddEvents = !!(onDateClick || onDateRangeSelect);

  // Handle mouse up globally to complete drag selection
  useEffect(() => {
    if (!dragState.isSelecting) {
      return;
    }

    const handleMouseUp = () => {
      if (dragState.startDate && dragState.endDate) {
        const start = dragState.startDate < dragState.endDate ? dragState.startDate : dragState.endDate;
        const end = dragState.startDate < dragState.endDate ? dragState.endDate : dragState.startDate;

        if (start.getTime() === end.getTime()) {
          // Single day click
          onDateClick?.(start);
        } else {
          // Multi-day drag
          onDateRangeSelect?.(start, end);
        }
      }
      setDragState({
        isSelecting: false,
        startDate: null,
        endDate: null,
        monthIndex: null,
        monthYear: null,
      });
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [dragState, onDateClick, onDateRangeSelect]);

  // Helper to check if a date is in the selection range
  const isDateInSelection = (year: number, month: number, day: number): boolean => {
    if (!dragState.isSelecting || !dragState.startDate || !dragState.endDate) return false;
    if (dragState.monthIndex !== month || dragState.monthYear !== year) return false;

    const date = new Date(year, month, day);
    const start = dragState.startDate < dragState.endDate ? dragState.startDate : dragState.endDate;
    const end = dragState.startDate < dragState.endDate ? dragState.endDate : dragState.startDate;

    return date >= start && date <= end;
  };

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const data = await calendarSettingsService.getAcademicYearTemplate();
        console.log('[AcademicYearCalendarView] Loaded template:', data);
        setTemplate(data);
      } catch (error) {
        console.error('Failed to load academic year template:', error);
      }
    };
    loadTemplate();
  }, []);

  useEffect(() => {
    const loadRamadan = async () => {
      try {
        const ramadan1 = await calendarSettingsService.getRamadanDatesForYear(academicYearStart);
        const ramadan2 = await calendarSettingsService.getRamadanDatesForYear(academicYearStart + 1);
        setRamadanDates(ramadan2 || ramadan1);
      } catch (error) {
        console.error('Failed to load Ramadan dates:', error);
      }
    };
    loadRamadan();
  }, [academicYearStart]);

  const academicStartMonth = useMemo(() => {
    return template?.startMonth ? template.startMonth - 1 : 8;
  }, [template]);

  // Academic year markers derived from template
  const academicMarkers = useMemo(() => {
    console.log('[AcademicYearCalendarView] Computing academicMarkers');
    console.log('[AcademicYearCalendarView] template:', JSON.stringify(template, null, 2));
    console.log('[AcademicYearCalendarView] academicYearStart:', academicYearStart);
    console.log('[AcademicYearCalendarView] academicStartMonth:', academicStartMonth);

    if (!template) {
      console.log('[AcademicYearCalendarView] No template loaded - markers will be empty');
      return [];
    }

    // Use ACADEMIC category color for all academic markers
    const academicCategory = categories.find(c => c.code === 'ACADEMIC');
    const academicColor = academicCategory?.color || '#3B82F6'; // Default to blue if no ACADEMIC category

    const markers: { date: Date; label: string; color: string; type: string }[] = [];
    const getYearForMonth = (monthIndex: number): number => {
      return monthIndex < academicStartMonth ? academicYearStart + 1 : academicYearStart;
    };

    // Academic year start
    if (template.startDay && template.startMonth) {
      const month = template.startMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.startDay),
        label: "Début Année Académique",
        color: academicColor,
        type: 'year-start'
      });
    }

    // Academic year end
    if (template.endDay && template.endMonth) {
      const month = template.endMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.endDay),
        label: "Fin Année Académique",
        color: academicColor,
        type: 'year-end'
      });
    }

    // Semester 1 start
    if (template.semester1StartDay && template.semester1StartMonth) {
      const month = template.semester1StartMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester1StartDay),
        label: "Début Semestre 1",
        color: academicColor,
        type: 'sem1-start'
      });
    }

    // Semester 1 end
    if (template.semester1EndDay && template.semester1EndMonth) {
      const month = template.semester1EndMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester1EndDay),
        label: "Fin Semestre 1",
        color: academicColor,
        type: 'sem1-end'
      });
    }

    // Semester 2 start
    if (template.semester2StartDay && template.semester2StartMonth) {
      const month = template.semester2StartMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester2StartDay),
        label: "Début Semestre 2",
        color: academicColor,
        type: 'sem2-start'
      });
    }

    // Semester 2 end
    if (template.semester2EndDay && template.semester2EndMonth) {
      const month = template.semester2EndMonth - 1;
      markers.push({
        date: new Date(getYearForMonth(month), month, template.semester2EndDay),
        label: "Fin Semestre 2",
        color: academicColor,
        type: 'sem2-end'
      });
    }

    console.log('[AcademicYearCalendarView] Generated markers:', markers.length, markers.map(m => ({
      type: m.type,
      label: m.label,
      date: m.date.toISOString().split('T')[0]
    })));
    return markers;
  }, [template, academicYearStart, academicStartMonth, categories]);

  const getYearForMonth = (monthIndex: number): number => {
    return monthIndex < academicStartMonth ? academicYearStart + 1 : academicYearStart;
  };

  const allMonths: MonthInfo[] = useMemo(() => {
    const months: MonthInfo[] = [];
    const startMonthIndex = academicStartMonth;

    for (let i = 0; i < 12; i++) {
      const monthIndex = (startMonthIndex + i) % 12;
      const year = getYearForMonth(monthIndex);
      months.push({ ...ALL_MONTHS[monthIndex], year });
    }

    return months;
  }, [academicYearStart, academicStartMonth]);

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const maxDays = 31;


  // Allocate lanes for events to minimize row height (only stack when events overlap)
  const allocateLanes = (bars: Omit<EventBar, 'lane'>[]): EventBar[] => {
    if (bars.length === 0) return [];

    // Sort by start day, then by duration (longer events first)
    const sortedBars = [...bars].sort((a, b) => {
      if (a.startDay !== b.startDay) return a.startDay - b.startDay;
      return (b.endDay - b.startDay) - (a.endDay - a.startDay);
    });

    const lanes: number[][] = []; // Each lane tracks which days are occupied

    return sortedBars.map(bar => {
      // Find the first lane where this event fits
      let assignedLane = -1;
      for (let laneIdx = 0; laneIdx < lanes.length; laneIdx++) {
        const lane = lanes[laneIdx];
        // Check if any day in this event's range is already occupied
        let canFit = true;
        for (let day = bar.startDay; day <= bar.endDay; day++) {
          if (lane.includes(day)) {
            canFit = false;
            break;
          }
        }
        if (canFit) {
          assignedLane = laneIdx;
          break;
        }
      }

      // If no existing lane fits, create a new one
      if (assignedLane === -1) {
        assignedLane = lanes.length;
        lanes.push([]);
      }

      // Mark the days as occupied in this lane
      for (let day = bar.startDay; day <= bar.endDay; day++) {
        lanes[assignedLane].push(day);
      }

      return { ...bar, lane: assignedLane };
    });
  };

  // Get event bars for a specific month
  const getEventBarsForMonth = (monthInfo: MonthInfo): EventBar[] => {
    const bars: Omit<EventBar, 'lane'>[] = [];
    const monthStart = new Date(monthInfo.year, monthInfo.index, 1);
    // End-of-day boundary: dates arrive as UTC midnight, which parses to 01:00
    // local (Morocco UTC+1) — a midnight monthEnd would drop last-day events
    const monthEnd = new Date(monthInfo.year, monthInfo.index + 1, 0, 23, 59, 59, 999);
    const daysInMonth = monthEnd.getDate();

    // Process academic markers first (they appear at the top)
    // Academic markers (semester start/end, year start/end) are always shown regardless of category filter
    // because they are structural calendar elements, not regular events
    academicMarkers.forEach(marker => {
      if (marker.date.getMonth() === monthInfo.index && marker.date.getFullYear() === monthInfo.year) {
        bars.push({
          event: { id: marker.type, name: marker.label } as unknown as PublicHoliday,
          type: 'marker',
          startDay: marker.date.getDate(),
          endDay: marker.date.getDate(),
          color: marker.color,
          title: marker.label,
          categoryLabel: 'Repère Académique',
        });
      }
    });

    // Process holidays - deduplicate by name, use VACATION category color
    // Only show if VACATION category is selected or no filter
    const vacationCategory = categories.find(c => c.code === 'VACATION');
    const holidayColor = vacationCategory?.color || '#10B981'; // Default to green if no VACATION category
    const seenHolidays = new Set<string>();
    holidays.forEach(holiday => {
      if (seenHolidays.has(holiday.name)) return;

      const hStart = new Date(holiday.startDate);
      const hEnd = new Date(holiday.endDate);

      // Check if holiday overlaps with this month
      if (hEnd >= monthStart && hStart <= monthEnd) {
        seenHolidays.add(holiday.name);
        const startDay = hStart < monthStart ? 1 : hStart.getDate();
        const endDay = hEnd > monthEnd ? daysInMonth : hEnd.getDate();

        bars.push({
          event: holiday,
          type: 'holiday',
          startDay,
          endDay,
          color: holidayColor,
          title: holiday.name,
          categoryLabel: 'Période de Vacances',
        });
      }
    });

    // NOTE: Ramadan is NOT shown as a bar - only as background color on calendar cells
    // See isRamadanDay() function and the cell rendering with 'bg-emerald-200/70' class

    // Process events
    events.forEach(event => {
      const eStart = new Date(event.startDate);
      const eEnd = new Date(event.endDate);

      // Check if event overlaps with this month
      if (eEnd >= monthStart && eStart <= monthEnd) {
        const startDay = eStart < monthStart ? 1 : eStart.getDate();
        const endDay = eEnd > monthEnd ? daysInMonth : eEnd.getDate();

        // Use category color by default, only use event.color if explicitly set
        const category = event.category || categories.find(c => c.id === event.categoryId);
        const color = event.color || category?.color || '#6B7280';

        bars.push({
          event,
          type: 'event',
          startDay,
          endDay,
          color,
          title: event.title,
          categoryLabel: category?.name || 'Événement',
        });
      }
    });

    // Allocate lanes to minimize stacking
    return allocateLanes(bars);
  };

  // Check if a date is during Ramadan
  const isRamadanDay = (year: number, month: number, day: number): boolean => {
    if (!ramadanDates) return false;
    const date = new Date(year, month, day);
    const ramadanStart = new Date(ramadanDates.startDate);
    const ramadanEnd = new Date(ramadanDates.endDate);
    return date >= ramadanStart && date <= ramadanEnd;
  };

  const isWeekend = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const isToday = (year: number, month: number, day: number): boolean => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  const goToCurrentAcademicYear = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const startYear = currentMonth >= academicStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    onYearChange(startYear);
  };

  return (
    <div className="space-y-4">
      {/* TeamUp-style Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onYearChange(academicYearStart - 1)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => onYearChange(academicYearStart + 1)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <button
            onClick={goToCurrentAcademicYear}
            className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            Aujourd'hui
          </button>
          <span className="text-sm font-medium text-gray-700 ml-2">
            {allMonths[0]?.abbr} {allMonths[0]?.year} - {allMonths[11]?.abbr} {allMonths[11]?.year}
          </span>
        </div>
      </div>

      {/* Calendar Grid - TeamUp Style */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white text-xs">
        {/* Days header row */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <div className="w-20 flex-shrink-0 px-2 py-1.5 font-medium text-gray-500 border-r border-gray-200">

          </div>
          <div className="flex-1 flex">
            {Array.from({ length: maxDays }, (_, i) => (
              <div
                key={i}
                className="flex-1 py-1.5 text-center font-medium text-gray-500 border-r border-gray-100 last:border-r-0"
                style={{ minWidth: '20px' }}
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Month rows */}
        {allMonths.map((monthInfo) => {
          const daysInThisMonth = getDaysInMonth(monthInfo.year, monthInfo.index);
          const eventBars = getEventBarsForMonth(monthInfo);
          // Calculate max lane for row height
          const maxLane = eventBars.length > 0 ? Math.max(...eventBars.map(b => b.lane)) + 1 : 0;

          return (
            <div
              key={`${monthInfo.year}-${monthInfo.index}`}
              className="flex border-b border-gray-100 last:border-b-0 relative"
              style={{ minHeight: `${Math.max(24, maxLane * 18 + 6)}px` }}
            >
              {/* Month label */}
              <div className="w-20 flex-shrink-0 px-2 py-1 font-medium text-gray-700 border-r border-gray-200 bg-gray-50">
                {monthInfo.name}
              </div>

              {/* Days grid with events */}
              <div className="flex-1 relative">
                {/* Day cells background */}
                <div className="flex absolute inset-0">
                  {Array.from({ length: maxDays }, (_, dayIndex) => {
                    const day = dayIndex + 1;
                    const isValidDay = day <= daysInThisMonth;
                    const weekend = isValidDay && isWeekend(monthInfo.year, monthInfo.index, day);
                    const today = isValidDay && isToday(monthInfo.year, monthInfo.index, day);
                    const ramadan = isValidDay && isRamadanDay(monthInfo.year, monthInfo.index, day);
                    const inSelection = isValidDay && isDateInSelection(monthInfo.year, monthInfo.index, day);

                    return (
                      <div
                        key={dayIndex}
                        className={`
                          flex-1 border-r border-gray-100 last:border-r-0 select-none
                          ${!isValidDay ? 'bg-gray-50' : ''}
                          ${inSelection ? 'bg-primary-200' : today && !inSelection ? 'bg-blue-50' : ramadan && isValidDay && !inSelection ? 'bg-emerald-200/70' : weekend && isValidDay && !inSelection ? 'bg-gray-50' : ''}
                          ${canAddEvents && isValidDay ? 'cursor-pointer hover:bg-primary-100' : ''}
                        `}
                        style={{ minWidth: '20px' }}
                        onMouseDown={(e) => {
                          if (canAddEvents && isValidDay) {
                            e.preventDefault();
                            const clickedDate = new Date(monthInfo.year, monthInfo.index, day);
                            setDragState({
                              isSelecting: true,
                              startDate: clickedDate,
                              endDate: clickedDate,
                              monthIndex: monthInfo.index,
                              monthYear: monthInfo.year,
                            });
                          }
                        }}
                        onMouseEnter={() => {
                          if (dragState.isSelecting && isValidDay &&
                              dragState.monthIndex === monthInfo.index &&
                              dragState.monthYear === monthInfo.year) {
                            setDragState(prev => ({
                              ...prev,
                              endDate: new Date(monthInfo.year, monthInfo.index, day),
                            }));
                          }
                        }}
                      />
                    );
                  })}
                </div>

                {/* Event bars */}
                <div className="relative z-10 py-0.5 px-0.5">
                  {eventBars.map((bar, idx) => {
                    const leftPercent = ((bar.startDay - 1) / maxDays) * 100;
                    const widthPercent = ((bar.endDay - bar.startDay + 1) / maxDays) * 100;
                    const isHovered = hoveredEvent === `${monthInfo.year}-${monthInfo.index}-${idx}`;
                    const startDate = new Date(monthInfo.year, monthInfo.index, bar.startDay);
                    const endDate = new Date(monthInfo.year, monthInfo.index, bar.endDay);

                    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const viewportHeight = window.innerHeight;
                      const showAbove = rect.bottom > viewportHeight / 2;
                      setHoveredEvent(`${monthInfo.year}-${monthInfo.index}-${idx}`);
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: showAbove ? rect.top : rect.bottom,
                        showAbove,
                      });
                    };

                    const handleMouseLeave = () => {
                      setHoveredEvent(null);
                      setTooltipPosition(null);
                    };

                    return (
                      <div
                        key={idx}
                        className={`absolute ${isHovered ? 'z-[100]' : ''}`}
                        style={{
                          left: `${leftPercent}%`,
                          width: `${widthPercent}%`,
                          top: `${bar.lane * 18 + 2}px`,
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div
                          className={`
                            h-4 rounded-sm text-[10px] font-medium text-white px-1 truncate
                            transition-all duration-100 cursor-pointer
                            ${isHovered ? 'shadow-lg ring-2 ring-white/50' : ''}
                          `}
                          style={{
                            backgroundColor: bar.color,
                            opacity: isHovered ? 1 : 0.9,
                          }}
                          onClick={() => {
                            if (bar.type === 'holiday' && onHolidayClick) {
                              onHolidayClick(bar.event as PublicHoliday);
                            } else if (bar.type === 'event') {
                              onEventClick(bar.event as AcademicEvent);
                            }
                          }}
                        >
                          {bar.title}
                        </div>

                        {/* Hover tooltip - rendered via portal */}
                        {isHovered && tooltipPosition && createPortal(
                          <div
                            ref={tooltipRef}
                            className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px] pointer-events-none"
                            style={{
                              left: tooltipPosition.x,
                              top: tooltipPosition.showAbove ? 'auto' : tooltipPosition.y + 8,
                              bottom: tooltipPosition.showAbove ? window.innerHeight - tooltipPosition.y + 8 : 'auto',
                              transform: 'translateX(-50%)',
                            }}
                          >
                            <div className="text-sm font-semibold text-gray-900 mb-1">
                              {bar.title}
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                              {startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                              {bar.startDay !== bar.endDay && ` - ${endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`}
                              {' '}{monthInfo.year}
                            </div>
                            <div
                              className="text-xs px-2 py-1 rounded inline-block font-medium"
                              style={{
                                backgroundColor: bar.color + '20',
                                color: bar.color,
                              }}
                            >
                              {bar.categoryLabel}
                            </div>
                            <div
                              className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent ${
                                tooltipPosition.showAbove
                                  ? 'top-full border-t-[6px] border-t-white'
                                  : 'bottom-full border-b-[6px] border-b-white'
                              }`}
                            />
                          </div>,
                          document.body
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expandable Legend Sections */}
      <div className="space-y-2 mt-4">
        {/* Category Sections - Show categories with their events */}
        {categories.filter(c => c.isActive).map(category => {
          // For VACATION category, include holidays + vacation events
          const isVacationCategory = category.code === 'VACATION';
          // For ACADEMIC category, include academic markers
          const isAcademicCategory = category.code === 'ACADEMIC';

          const categoryEvents = events.filter(e =>
            (e.categoryId === category.id) || (e.category?.id === category.id) || (e.category?.code === category.code)
          );

          // Combine items for the category
          const allItems: Array<{ id: string; title: string; startDate: string; isHoliday: boolean; isMarker?: boolean }> = [];

          // Calculate academic year date bounds correctly
          // Academic year 2025-2026 with start month October (9):
          // - Starts: October 1, 2025 (academicYearStart=2025, academicStartMonth=9)
          // - Ends: September 30, 2026 (last day before next academic year starts)
          const academicYearStartDate = new Date(academicYearStart, academicStartMonth, 1);
          // End date is the last day of the month BEFORE the start month of NEXT year
          // e.g., for Oct start: end is Sept 30 of next year = new Date(2026, 9, 0) = Sept 30, 2026
          const academicYearEndDate = new Date(academicYearStart + 1, academicStartMonth, 0);

          // For ACADEMIC category, add academic markers (from template)
          if (isAcademicCategory) {
            academicMarkers.forEach(marker => {
              allItems.push({
                id: `marker-${marker.type}`,
                title: marker.label,
                startDate: marker.date.toISOString(),
                isHoliday: false,
                isMarker: true,
              });
            });
          }

          if (isVacationCategory) {
            // Add holidays that fall within the academic year, deduplicated by name+date
            const seenHolidayKeys = new Set<string>();
            holidays.forEach(h => {
              const holidayDate = new Date(h.startDate);
              // Normalize dates to midnight for comparison (avoid timezone issues)
              const holidayTime = new Date(holidayDate.getFullYear(), holidayDate.getMonth(), holidayDate.getDate()).getTime();
              const startTime = academicYearStartDate.getTime();
              const endTime = academicYearEndDate.getTime();

              // Check if holiday is within academic year bounds
              if (holidayTime >= startTime && holidayTime <= endTime) {
                // Create unique key based on name and date to avoid duplicates
                const dateKey = `${holidayDate.getFullYear()}-${holidayDate.getMonth()}-${holidayDate.getDate()}`;
                const uniqueKey = `${h.name}-${dateKey}`;

                if (!seenHolidayKeys.has(uniqueKey)) {
                  seenHolidayKeys.add(uniqueKey);
                  allItems.push({
                    id: `holiday-${h.id}`,
                    title: h.name,
                    startDate: h.startDate,
                    isHoliday: true,
                  });
                }
              }
            });
          }

          // Add category events (also filter by academic year bounds)
          categoryEvents.forEach(e => {
            const eventDate = new Date(e.startDate);
            const eventTime = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime();
            const startTime = academicYearStartDate.getTime();
            const endTime = academicYearEndDate.getTime();

            // Only include events within the academic year
            if (eventTime >= startTime && eventTime <= endTime) {
              allItems.push({
                id: e.id,
                title: e.title,
                startDate: e.startDate,
                isHoliday: false,
              });
            }
          });

          if (allItems.length === 0) return null;

          // Sort by academic year order (items in academic year months first)
          const sortedItems = [...allItems].sort((a, b) => {
            const dateA = new Date(a.startDate);
            const dateB = new Date(b.startDate);

            // Get month index adjusted for academic year (Sept=0, Oct=1, ..., Aug=11)
            const getAcademicMonthIndex = (date: Date) => {
              const month = date.getMonth();
              return month >= academicStartMonth ? month - academicStartMonth : month + (12 - academicStartMonth);
            };

            const academicMonthA = getAcademicMonthIndex(dateA);
            const academicMonthB = getAcademicMonthIndex(dateB);

            // First sort by academic year position
            if (academicMonthA !== academicMonthB) {
              return academicMonthA - academicMonthB;
            }
            // Then by day within month
            return dateA.getDate() - dateB.getDate();
          });

          return (
            <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => {
                  const newSet = new Set(expandedSections);
                  if (newSet.has(category.id)) {
                    newSet.delete(category.id);
                  } else {
                    newSet.add(category.id);
                  }
                  setExpandedSections(newSet);
                }}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: category.color }} />
                  <span className="text-sm font-medium text-gray-700">{category.name}</span>
                  <span className="text-xs text-gray-500">({allItems.length})</span>
                </div>
                {expandedSections.has(category.id) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedSections.has(category.id) && (
                <div className="px-3 py-2 space-y-1 bg-white max-h-64 overflow-y-auto">
                  {sortedItems.map(item => (
                    <div
                      key={item.id}
                      className={`flex items-center gap-2 text-xs py-1 rounded px-1 -mx-1 ${
                        !item.isHoliday && !item.isMarker ? 'cursor-pointer hover:bg-gray-50' : ''
                      }`}
                      onClick={() => {
                        if (!item.isHoliday && !item.isMarker) {
                          const event = categoryEvents.find(e => e.id === item.id);
                          if (event) onEventClick(event);
                        } else if (item.isHoliday) {
                          const holiday = holidays.find(h => `holiday-${h.id}` === item.id);
                          if (holiday && onHolidayClick) onHolidayClick(holiday);
                        }
                      }}
                    >
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-gray-600 w-28 flex-shrink-0">
                        {new Date(item.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <span className="text-gray-800 font-medium truncate">{item.title}</span>
                      {item.isHoliday && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{
                            backgroundColor: category.color + '20',
                            color: category.color
                          }}
                        >Férié</span>
                      )}
                      {item.isMarker && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{
                            backgroundColor: category.color + '20',
                            color: category.color
                          }}
                        >Repère</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Ramadan indicator */}
        {ramadanDates && (
          <div className="flex items-center gap-2 px-3 py-2 text-xs bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="w-3 h-3 rounded-sm bg-emerald-300" />
            <span className="text-emerald-700 font-medium">Ramadan:</span>
            <span className="text-emerald-600">
              {new Date(ramadanDates.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} - {new Date(ramadanDates.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
